import { useData } from '../../contexts/DataContext';
import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll'
import { useLanguage } from '../../contexts/LanguageContext'; // Import useLanguage hook
import LanguageToggleButton from '../Common/LanguageToggleButton'
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
// Import icons
import { FaPhone, FaWhatsapp, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { BsFacebook, BsInstagram, BsLinkedin } from 'react-icons/bs';
import { FaXTwitter } from "react-icons/fa6";
import SocialIcons from '../Common/SocialIcons';

const Footer = () => {
  const { t } = useTranslation();
  const result = useData(); // Get data from context
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { language, toggleLanguage } = useLanguage()
  const isRTL = language === 'ar';
  const { site_info, navigation, contact_info, social_media } = result.data;
  const location = useLocation();
  const navigate = useNavigate();

  const renderSocialIcons = () => (
    <SocialIcons socialMedia={social_media} translationFunc={t} translationKeyPrefix="contact.social" />
  );

  const services = Array.isArray(result.data?.services)
    ? result.data.services
    : Object.values(result.data?.services || {});

  const scrollConfig = {
    smooth: true,
    duration: 800, // Increased duration for smoother scroll
    spy: true,
    offset: -100, // Adjusted offset
    activeClass: 'active1',
    ignoreCancelEvents: false,
    spyThrottle: 500
  }

  // Add this function to handle active state
  const [activeSection, setActiveSection] = useState('home')

  const handleSetActive = (to) => {
    setActiveSection(to)
  }

  const closeMenu = () => setIsOpen(false)

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const LinkContent = ({ item }) => (
    <>
      <span>{item.title}</span>
    </>
  );



  const handleNavigation = (item) => {
    closeMenu();
    //console.log('Navigation item:', item);
    //console.log('Current location:', location.pathname);

    if (item.to && location.pathname !== '/') {
      // First navigate to homepage
      navigate(item.href);

      // Then set up a timeout to scroll after navigation is complete
      setTimeout(() => {
        const element = document.getElementById(item.to);
        if (element) {
          const yOffset = -100;
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
          setActiveSection(item.to);
        }
      }, 100); // Small delay to ensure DOM is updated
    } else if (item.to && location.pathname === '/') {
      // If already on homepage, just scroll to section
      const element = document.getElementById(item.to);
      if (element) {
        const yOffset = -100;
        const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: yPosition, behavior: 'smooth' });
        setActiveSection(item.to);
      }
    } else if (item.href) {
      // Regular navigation to other pages
      navigate(item.href);
      setActiveSection(item.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Handle scroll restoration and navigation state
    if (location.state?.scrollTo) {
      const elementId = location.state.scrollTo === 'root' ? 'root' : location.state.scrollTo; // Handle 'root' as 'root'
      //console.log('Restoring scroll to section:', elementId);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const yOffset = -100; // Adjust this value based on your header height
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          //console.log('Restoring scroll to position:', yPosition);
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
        } else {
          //console.log('Element not found for restoration:', elementId);
        }
        // Clear the state
        navigate(location.pathname, { replace: true, state: {} });
      }, 500);
    } else {
      setActiveSection(location.pathname);
    }
  }, [location, navigate]);

  const NavigationItem = ({ item, className }) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    if (item.to && isHomePage) {
      return (
        <ScrollLink
          to={item.to}
          href={item.href || `/#${item.to}`} // Add href for crawlability
          {...scrollConfig}
          onSetActive={() => handleSetActive(item.to)}
          className={className}
          onClick={() => handleNavigation(item)}
        >
          <LinkContent item={item} />
        </ScrollLink>
      );
    }

    return (
      <RouterLink
        to={item.href || '/'}
        href={item.href || `/#${item.to}`} // Add href for crawlability
        onClick={() => handleNavigation(item)}
        className={className}
      >
        <LinkContent item={item} />
      </RouterLink>
    );
  };

  return (
    <footer className="text-white bg-gray-700/30">
      <div className="container px-4 py-16 mx-auto">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Logo Column */}
          <div className="space-y-6">
            <img src="assets/footer_logo.webp" alt="Logo" className="h-16" />
            <p className="text-[var(--secondary-color)]">
              {site_info.footer_description}
            </p>
            {/* Social Media Links */}
            <div className="pt-6 border-stone-100">
              <div className="flex gap-4">
                {renderSocialIcons()}
              </div>
            </div>
          </div>

          {/* Pages Column */}
          <div>
            <h3 className="pb-2 mb-6 text-lg font-semibold border-b border-[var(--primary-color)]">{t('footer.navigation')}</h3>
            <ul className="space-y-3 text-right">
              {navigation.map((item) => (
                <li key={item.id}>
                  <NavigationItem
                    item={item}
                    className={`flex gap-2 items-center transition-colors cursor-pointer`}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="pb-2 mb-6 text-lg font-semibold border-b border-[var(--primary-color)]">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <NavigationItem
                    item={{
                      id: service.id,
                      title: service.title,
                      href: `/service/${service.slug}`,
                    }}
                    className="text-[var(--secondary-color)] transition-colors hover:text-white"
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="pb-2 mb-6 text-lg font-semibold border-b border-[var(--primary-color)]">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-[var(--secondary-color)]">
                <a href={`mailto:${contact_info.email}`} title={t('contact.email')} className={`flex items-center text-xs text-white sm:text-sm md:text-base`}>
                  <FaEnvelope className={`${isRTL ? 'mr-2' : 'mr-2'} text-xs sm:text-sm md:text-base`} />
                  <span dir="ltr">{contact_info.email}</span>
                </a>
              </li>
              <li className="flex items-center text-[var(--secondary-color)]">
                <a href={`tel:${contact_info.phone}`} title={t('contact.phone')} className={`flex items-center text-xs text-white sm:text-sm md:text-base`}>
                  <FaPhone className={`${isRTL ? 'mr-2' : 'mr-2'} text-xs sm:text-sm md:text-base`} />
                  <span dir="ltr">{contact_info.phone}</span>
                </a>
              </li>
              <li className="flex items-center text-[var(--secondary-color)]">
                <div className={`flex items-center text-xs text-white sm:text-sm md:text-base`}>
                  <FaMapMarkerAlt className={`${isRTL ? 'mr-2' : 'mr-2'} text-xs sm:text-sm md:text-base`} />
                  <span dir="ltr">{contact_info.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="container px-4 py-6 mx-auto">
          <p className="text-center text-[var(--secondary-color)]">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer;